import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  GitBranch, TrendingDown, AlertTriangle, CheckCircle, Clock, RefreshCw,
  Loader2, Target, ArrowRight, Flag, Zap, Bell, MoreVertical, EyeOff, Eye,
  Pause, Play, XCircle, RotateCcw, LayoutGrid, Rows, ChevronDown, ChevronRight, FolderTree
} from "lucide-react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, LineChart, ReferenceLine
} from "recharts";

const STATUS_CFG = {
  planning:  { label: "Planificación", cls: "bg-blue-100 text-blue-700",    dot: "bg-blue-400" },
  active:    { label: "Activo",         cls: "bg-green-100 text-green-700",  dot: "bg-green-500" },
  completed: { label: "Completado",     cls: "bg-gray-100 text-gray-600",   dot: "bg-gray-400" },
  closed:    { label: "Cerrado",        cls: "bg-gray-100 text-gray-600",   dot: "bg-gray-400" },
  paused:    { label: "Pausado",        cls: "bg-amber-100 text-amber-700", dot: "bg-amber-400" },
  cancelled: { label: "Cancelado",      cls: "bg-rose-100 text-rose-700",   dot: "bg-rose-400" },
};

function MiniProgress({ pct, delayed }) {
  const color = delayed ? "#dc2626" : pct === 100 ? "#16a34a" : pct >= 60 ? "#007934" : "#d97706";
  return (
    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

const CustomTooltipVelocity = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  // Iter205 · Muestra el nombre completo del sprint (Azure) si está disponible
  const fullName = payload[0]?.payload?.sprint_full_name;
  const fullProj = payload[0]?.payload?.fullName;
  const displayLabel = fullName ? `${label} · ${fullName}` : (fullProj || label);
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-lg p-3 text-xs max-w-xs">
      <p className="font-semibold text-gray-700 mb-1.5">{displayLabel}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color: p.color }}>{p.name}: <b>{p.value}</b></p>
      ))}
    </div>
  );
};

export default function SprintDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [velocityData, setVelocityData] = useState({});   // project_id → velocity[]
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showHidden, setShowHidden] = useState(false);
  // Iter258 (Mensaje 1) · por defecto se ocultan los sprints cerrados/histórico
  const [includeClosed, setIncludeClosed] = useState(false);
  const [unassignedModal, setUnassignedModal] = useState(null); // { sprintId, sprintName, items, loading }
  const [menuOpen, setMenuOpen] = useState(null); // sprint id whose action menu is open
  const [actionBusy, setActionBusy] = useState(null); // sprint id being updated
  // Iter208 · Alterna entre vista Grid (cards) y Table (compacta)
  const [layoutMode, setLayoutMode] = useState(() => {
    try { return localStorage.getItem("sprint_layout_mode") || "grid"; } catch { return "grid"; }
  });
  useEffect(() => {
    try { localStorage.setItem("sprint_layout_mode", layoutMode); } catch (_) { /* noop */ }
  }, [layoutMode]);
  // Iter209 · Agrupación plegable por proyecto en la vista tabla
  const [groupByProject, setGroupByProject] = useState(() => {
    try { return localStorage.getItem("sprint_group_by_project") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("sprint_group_by_project", groupByProject ? "1" : "0"); } catch (_) { /* noop */ }
  }, [groupByProject]);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const stored = localStorage.getItem("sprint_collapsed_groups");
      if (stored) return new Set(JSON.parse(stored));
    } catch (_) { /* noop */ }
    return new Set();
  });
  // Iter211 · Persistir qué grupos quedaron plegados
  useEffect(() => {
    try {
      localStorage.setItem("sprint_collapsed_groups", JSON.stringify([...collapsedGroups]));
    } catch (_) { /* noop */ }
  }, [collapsedGroups]);
  const toggleGroup = (pid) => setCollapsedGroups(prev => {
    const n = new Set(prev);
    if (n.has(pid)) n.delete(pid); else n.add(pid);
    return n;
  });
  // Iter210 · Selección de proyecto en el Velocity Chart consolidado
  const [selectedVelocityProject, setSelectedVelocityProject] = useState(() => {
    try { return localStorage.getItem("sprint_velocity_project") || ""; } catch { return ""; }
  });
  useEffect(() => {
    try {
      if (selectedVelocityProject) localStorage.setItem("sprint_velocity_project", selectedVelocityProject);
    } catch (_) { /* noop */ }
  }, [selectedVelocityProject]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: bench }] = await Promise.all([
        axios.get(`${API}/sprint-dashboard`, { params: includeClosed ? { include_closed: 1 } : {} }),
        axios.get(`${API}/projects/velocity-benchmark`),
      ]);
      setData(d);
      // Build velocity map directly from benchmark data (no extra N calls)
      const velMap = {};
      bench.forEach(proj => { velMap[proj.project_id] = proj.sprints; });
      setVelocityData(velMap);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [includeClosed]);

  useEffect(() => { load(); }, [load]);

  const openUnassigned = async (s) => {
    setUnassignedModal({ sprintId: s.id, sprintName: `${s.project_title} · Sprint ${s.sprint_number}: ${s.name}`, items: [], loading: true });
    try {
      const { data: r } = await axios.get(`${API}/sprints/${s.id}/workitems`, { params: { unassigned: 1 } });
      setUnassignedModal(prev => prev && prev.sprintId === s.id ? { ...prev, items: r.items, loading: false } : prev);
    } catch (e) {
      setUnassignedModal(prev => prev && prev.sprintId === s.id ? { ...prev, loading: false } : prev);
    }
  };

  const handleNotify = async (sid) => {
    if (!window.confirm("¿Enviar notificación de retraso a dredondo@valorizasm.com?")) return;
    setNotifying(sid);
    try {
      const { data: r } = await axios.post(`${API}/sprints/${sid}/notify-delayed`);
      alert(`Notificación enviada · ${r.open_items} items abiertos`);
    } catch (e) { alert("Error al enviar notificación: " + (e.response?.data?.detail || e.message)); }
    finally { setNotifying(null); }
  };

  const setManualStatus = async (sid, value, label) => {
    setActionBusy(sid);
    setMenuOpen(null);
    try {
      await axios.patch(`${API}/sprints/${sid}/manual-status`, null, { params: { value } });
      await load();
    } catch (e) {
      alert(`No se pudo marcar como ${label}: ` + (e.response?.data?.detail || e.message));
    } finally { setActionBusy(null); }
  };

  const toggleHidden = async (sid) => {
    setActionBusy(sid);
    setMenuOpen(null);
    try {
      await axios.patch(`${API}/sprints/${sid}/toggle-hidden`);
      await load();
    } catch (e) {
      alert("No se pudo cambiar la visibilidad: " + (e.response?.data?.detail || e.message));
    } finally { setActionBusy(null); }
  };

  const [syncing, setSyncing] = React.useState(false);
  const handleSyncSprints = async () => {
    setSyncing(true);
    try {
      const { data: r } = await axios.post(`${API}/devops/sync-sprints`);
      if (r.error) {
        alert("Error: " + r.error);
      } else {
        alert("Sincronización iniciada en segundo plano — refresca en unos segundos.");
        // Reintentar refresco de datos en 6s (background job de sprint sync)
        setTimeout(() => load(), 6000);
      }
    } catch (e) {
      alert("Error al iniciar sync: " + (e.response?.data?.detail || e.message));
    } finally { setSyncing(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={32} className="animate-spin text-[#007934]" />
    </div>
  );

  const { sprints = [], summary = {}, delay_ranking = [], assignee_distribution = [], forecast = [] } = data || {};
  const hiddenCount = sprints.filter(s => s.is_hidden).length;
  const visibleSprints = showHidden ? sprints : sprints.filter(s => !s.is_hidden);
  const filtered = filterStatus === "all" ? visibleSprints : visibleSprints.filter(s => s.status === filterStatus);
  const delayedSprints = sprints.filter(s => s.is_delayed && !s.is_hidden);
  const allProjects = [...new Map(sprints.map(s => [s.project_id, { id: s.project_id, title: s.project_title }])).values()].filter(p => p.id);

  return (
    <div className="space-y-5" data-testid="sprint-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Dashboard de Sprints</h1>
          <p className="text-sm text-gray-500 mt-0.5">Vista global de todos los sprints activos y rendimiento del equipo</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSyncSprints} disabled={syncing}
            className="px-3 py-2 border border-[#007934]/30 text-[#007934] rounded-lg hover:bg-[#007934]/10 transition-all text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60"
            data-testid="sync-sprints-btn"
            title="Fuerza un pull desde Azure DevOps (los sprints se refrescan automáticamente cada 6h)">
            {syncing ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
            {syncing ? "Sincronizando..." : "Sincronizar desde Azure"}
          </button>
          <button onClick={load} className="p-2.5 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all" data-testid="refresh-btn">
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="summary-kpis">
        {[
          { label: "Sprints activos",     val: summary.active_sprints,          icon: GitBranch,     cls: "text-[#007934]" },
          { label: "Total sprints",       val: summary.total_sprints,           icon: Flag,          cls: "text-blue-600" },
          { label: "En riesgo",           val: summary.at_risk_sprints,         icon: AlertTriangle, cls: summary.at_risk_sprints > 0 ? "text-red-600" : "text-gray-400" },
          { label: "Avance global",       val: `${summary.global_velocity_pct}%`,icon: Zap,           cls: "text-[#007934]" },
        ].map(k => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0">
                <Icon size={18} className={k.cls} />
              </div>
              <div>
                <p className={`text-2xl font-bold font-['Outfit'] ${k.cls}`}>{k.val}</p>
                <p className="text-xs text-gray-500">{k.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* P01 (roadmap-valoriza-revision-despliegue-2026-10-12, E18) ·
          "0 en riesgo" no equivale a "0 sanos confirmados" cuando la
          mayoría de sprints activos no tiene base de velocidad suficiente
          (< 3 días transcurridos o < 2 ítems cerrados) para evaluar riesgo. */}
      {summary.sprints_sin_base_para_evaluar > 0 && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-800" data-testid="sprints-sin-base-banner">
          <AlertTriangle size={13} className="flex-shrink-0 text-amber-600" />
          <span>
            <strong>{summary.sprints_sin_base_para_evaluar}</strong> de {summary.active_sprints} sprints activos aún no tienen base de velocidad suficiente (mínimo 3 días transcurridos y 2 ítems cerrados) para evaluar si están en riesgo — &ldquo;{summary.at_risk_sprints} en riesgo&rdquo; es sobre los que SÍ se pudieron evaluar, no una garantía de que el resto esté sano.
          </span>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          Iter207 · Bloques PM operativo: Ranking retrasos · Distribución
          por assignee · Predicción de cierre. Diseñado para toma de
          decisiones rápidas ("qué hago hoy y quién carga más").
          ═══════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* (1) Ranking de retrasos */}
        <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="delay-ranking-block">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center">
                <TrendingDown size={14} className="text-rose-600"/>
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900 font-['Outfit']">Ranking de retrasos</h3>
                <p className="text-[10px] text-gray-400">Sprints activos con fecha fin vencida</p>
              </div>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">{delay_ranking.length}</span>
          </div>
          {delay_ranking.length === 0 ? (
            <div className="text-center py-6 text-xs text-gray-400 flex flex-col items-center gap-1">
              <CheckCircle size={20} className="text-emerald-500"/>
              Ningún sprint activo va retrasado
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {delay_ranking.slice(0, 8).map(r => (
                <div key={r.sprint_id} className="border border-rose-100 bg-rose-50/40 rounded-lg px-2.5 py-2 text-xs"
                     data-testid={`delay-rank-item-${r.sprint_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-gray-800 truncate">{r.project_title}</span>
                    <span className="text-rose-700 font-bold whitespace-nowrap">+{r.delay_days}d</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1 text-[10px] text-gray-500">
                    <span className="truncate">S{r.sprint_number} · {r.sprint_name}</span>
                    <span className="whitespace-nowrap">{r.remaining_items} abiertos</span>
                  </div>
                  <MiniProgress pct={r.completion_pct} delayed/>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* (2) Distribución work items por assignee */}
        <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="assignee-distribution-block">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Target size={14} className="text-indigo-600"/>
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900 font-['Outfit']">Carga por asignatario</h3>
                <p className="text-[10px] text-gray-400">Work items abiertos en proyectos activos</p>
              </div>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{assignee_distribution.length}</span>
          </div>
          {assignee_distribution.length === 0 ? (
            <div className="text-center py-6 text-xs text-gray-400">Sin datos de asignación</div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {(() => {
                const maxOpen = Math.max(...assignee_distribution.map(a => a.open), 1);
                return assignee_distribution.map(a => {
                  const width = Math.round((a.open / maxOpen) * 100);
                  return (
                    <div key={a.assignee} className="text-xs" data-testid={`assignee-row-${a.assignee.replace(/\s/g,"_")}`}>
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className="text-gray-800 truncate flex-1" title={a.assignee}>{a.assignee}</span>
                        <span className="text-[10px] text-gray-500 whitespace-nowrap">
                          <b className="text-indigo-700">{a.open}</b>
                          <span className="mx-0.5">abiertos</span>
                          · <span className="text-emerald-600">{a.closed}</span> cerrados
                          {a.bugs_open > 0 && <> · <span className="text-rose-600">{a.bugs_open} bugs</span></>}
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-400" style={{ width: `${width}%` }}/>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>

        {/* (3) Predicción de cierre */}
        <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="forecast-block">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Clock size={14} className="text-emerald-600"/>
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900 font-['Outfit']">Predicción de cierre</h3>
                <p className="text-[10px] text-gray-400">ETA según velocidad actual · sprints activos</p>
              </div>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{forecast.length}</span>
          </div>
          {forecast.length === 0 ? (
            <div className="text-center py-6 text-xs text-gray-400">Sin sprints activos</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {forecast.map(f => {
                const onTime = f.will_finish_on_time;
                const noBase = f.will_finish_on_time === null;
                const cardCls = noBase ? "border-gray-200 bg-gray-50/60" : onTime ? "border-emerald-100 bg-emerald-50/40" : "border-amber-100 bg-amber-50/40";
                const pctCls = noBase ? "text-gray-500" : onTime ? "text-emerald-700" : "text-amber-700";
                return (
                  <div key={f.sprint_id}
                       className={`border rounded-lg px-2.5 py-2 text-xs ${cardCls}`}
                       data-testid={`forecast-item-${f.sprint_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-gray-800 truncate">{f.project_title}</span>
                      <span className={`whitespace-nowrap font-bold ${pctCls}`}>
                        {noBase ? "— base insuficiente" : `${onTime ? "✓" : "⚠"} ${f.projected_pct_at_end}%`}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-1 text-[10px] text-gray-500">
                      <span className="truncate">S{f.sprint_number} · {f.velocity_per_day} items/día{noBase ? " (muy pocos datos aún)" : ""}</span>
                      <span className="whitespace-nowrap">
                        {f.eta_date ? `ETA ${f.eta_date}` : "Sin ritmo"} · Fin plan {f.end_date}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Delay alerts */}
      {delayedSprints.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="delay-alerts">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={15} className="text-red-600" />
            <span className="text-sm font-semibold text-red-700">{delayedSprints.length} sprint(s) con items retrasados</span>
          </div>
          <div className="space-y-2">
            {delayedSprints.map(s => (
              <div key={s.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-red-100">
                <div>
                  <span className="text-sm text-red-700 font-medium">{s.project_title} · Sprint {s.sprint_number}: {s.name}</span>
                  <span className="text-xs text-gray-400 ml-2">Fin: {s.end_date} · {s.remaining_items} items abiertos</span>
                </div>
                <button onClick={() => handleNotify(s.id)} disabled={notifying === s.id}
                  className="flex items-center gap-1.5 text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 disabled:opacity-50"
                  data-testid={`notify-delayed-${s.id}`}>
                  {notifying === s.id ? <Loader2 size={11} className="animate-spin" /> : <Bell size={11} />}
                  Notificar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Iter210 · Velocity Chart consolidado.
          - Se retiró el "Benchmarking de Velocidad entre Proyectos" (no aportaba insights accionables).
          - Los charts por proyecto (uno por card) se agrupan en un único bloque con desplegable. */}
      {(() => {
        const projectsWithVel = allProjects.filter(p => (velocityData[p.id] || []).length > 0);
        if (projectsWithVel.length === 0) return null;
        const currentProjectId = projectsWithVel.some(p => p.id === selectedVelocityProject)
          ? selectedVelocityProject
          : projectsWithVel[0].id;
        const currentProject = projectsWithVel.find(p => p.id === currentProjectId);
        const vel = velocityData[currentProjectId] || [];
        return (
          <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="velocity-chart-consolidated">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <h2 className="text-sm font-bold text-gray-900 font-['Outfit']">Velocity Chart por proyecto</h2>
                <p className="text-xs text-gray-400 mt-0.5">Items completados vs planificados por sprint (nombres tal como aparecen en Azure DevOps)</p>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[10px] text-gray-500 uppercase tracking-wide">Proyecto</label>
                <select
                  value={currentProjectId}
                  onChange={e => setSelectedVelocityProject(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs bg-white min-w-[200px]"
                  data-testid="velocity-project-select">
                  {projectsWithVel.map(p => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
                {currentProject && (
                  <button onClick={() => navigate(`/projects/${currentProject.id}`)}
                    className="text-xs text-[#007934] hover:underline flex items-center gap-1 whitespace-nowrap"
                    data-testid="velocity-project-open">
                    Ver proyecto <ArrowRight size={12} />
                  </button>
                )}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={vel} margin={{ top: 5, right: 20, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="sprint_name" tick={{ fontSize: 11, fill: "#9ca3af" }} interval={0} angle={-25} textAnchor="end" height={40} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} allowDecimals={false} />
                <Tooltip content={<CustomTooltipVelocity />} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: "11px" }} />
                <Bar dataKey="total" name="Planificados" fill="#E8F5E9" radius={[4, 4, 0, 0]} />
                <Bar dataKey="completed" name="Completados" fill="#007934" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="completion_pct" name="Velocidad %" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: "#f59e0b" }} yAxisId={0} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        );
      })()}

      {/* Sprint cards grid */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-gray-700">Todos los sprints ({filtered.length})</h2>
          <div className="flex items-center gap-1.5 flex-wrap">
            {["all", "active", "planning", "paused", "cancelled", "completed"].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                className={`text-xs px-3 py-1.5 rounded-lg transition-all ${filterStatus === s ? "bg-[#007934] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                data-testid={`filter-status-${s}`}>
                {s === "all" ? "Todos"
                  : s === "active" ? "Activos"
                  : s === "planning" ? "Planificación"
                  : s === "paused" ? "Pausados"
                  : s === "cancelled" ? "Cancelados"
                  : "Completados"}
              </button>
            ))}
            <button onClick={() => setIncludeClosed(v => !v)}
              className={`text-xs px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 border ${includeClosed ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}
              data-testid="toggle-include-closed"
              title={includeClosed ? "Ocultar histórico cerrado" : "Ver también sprints cerrados/histórico"}>
              {includeClosed ? <Eye size={12} /> : <EyeOff size={12} />}
              {includeClosed ? "Ocultar histórico" : `Ver cerrados/histórico${summary.closed_sprints_hidden ? ` (${summary.closed_sprints_hidden})` : ""}`}
            </button>
            <button onClick={() => setShowHidden(v => !v)}
              className={`text-xs px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 border ${showHidden ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}
              data-testid="toggle-show-hidden"
              title={showHidden ? "Ocultar los sprints marcados como ocultos" : "Mostrar los sprints ocultos"}>
              {showHidden ? <Eye size={12} /> : <EyeOff size={12} />}
              {showHidden ? `Ocultando ${hiddenCount}` : `Mostrar ocultos${hiddenCount ? ` (${hiddenCount})` : ""}`}
            </button>
            {/* Iter208 · Layout toggle: Grid (cards) ↔ Table (compact) */}
            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden" data-testid="layout-toggle">
              <button
                onClick={() => setLayoutMode("grid")}
                className={`px-2.5 py-1.5 flex items-center gap-1 text-xs transition ${layoutMode === "grid" ? "bg-[#007934] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                data-testid="layout-grid-btn"
                title="Vista cards (por defecto)">
                <LayoutGrid size={12}/> Grid
              </button>
              <button
                onClick={() => setLayoutMode("table")}
                className={`px-2.5 py-1.5 flex items-center gap-1 text-xs transition ${layoutMode === "table" ? "bg-[#007934] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                data-testid="layout-table-btn"
                title="Vista tabla compacta (para navegar rápido con muchos sprints)">
                <Rows size={12}/> Tabla
              </button>
            </div>
            {/* Iter209 · Agrupar por proyecto (sólo tabla) */}
            {layoutMode === "table" && (
              <button onClick={() => setGroupByProject(v => !v)}
                className={`text-xs px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1.5 border ${groupByProject ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}
                data-testid="toggle-group-by-project"
                title={groupByProject ? "Volver al listado plano" : "Agrupar sprints por proyecto plegable"}>
                <FolderTree size={12}/>
                {groupByProject ? "Agrupado" : "Agrupar por proyecto"}
              </button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-gray-200 flex flex-col items-center justify-center py-16 text-gray-400">
            <GitBranch size={36} className="mb-3 opacity-20" />
            <p className="text-sm">Sin sprints en esta categoría</p>
            <p className="text-xs mt-1">Crea sprints en la vista de detalle de cada proyecto</p>
          </div>
        ) : layoutMode === "table" ? (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid="sprint-table-view">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="sprint-table">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr className="text-left text-gray-500">
                    <th className="px-3 py-2 font-semibold">Proyecto / Sprint</th>
                    <th className="px-2 py-2 font-semibold text-center">Estado</th>
                    <th className="px-2 py-2 font-semibold text-right">%</th>
                    <th className="px-2 py-2 font-semibold text-right">Items</th>
                    <th className="px-2 py-2 font-semibold text-right">SP</th>
                    <th className="px-2 py-2 font-semibold text-center">Fechas</th>
                    <th className="px-2 py-2 font-semibold text-center w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Función para renderizar una fila individual de sprint
                    const renderSprintRow = (s) => {
                      const cfg = STATUS_CFG[s.status] || STATUS_CFG.planning;
                      const dimmed = s.is_hidden || s.status === "cancelled";
                      const manualStatus = (s.manual_status || "").toLowerCase();
                      const isMenuOpen = menuOpen === s.id;
                      const isBusy = actionBusy === s.id;
                      return (
                        <tr key={s.id}
                            className={`border-b border-gray-50 hover:bg-emerald-50/30 transition ${dimmed ? "opacity-50" : ""} ${s.is_delayed ? "bg-red-50/40" : ""}`}
                            data-testid={`sprint-row-${s.id}`}>
                          <td className="px-3 py-2">
                            {!groupByProject && <div className="text-gray-400 text-[10px] truncate max-w-xs">{s.project_title}</div>}
                            <div className={`text-gray-800 font-semibold truncate max-w-xs ${groupByProject ? "pl-4" : ""}`} title={s.name}>S{s.sprint_number} · {s.name}</div>
                          </td>
                          <td className="px-2 py-2 text-center">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cfg.cls}`}>{cfg.label}</span>
                            {s.is_empty && <span className="ml-1 text-[9px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded" data-testid={`sprint-empty-badge-${s.id}`}>Vacío</span>}
                            {s.is_delayed && <span className="ml-1 text-[9px] bg-red-100 text-red-700 px-1 py-0.5 rounded font-medium">Retraso</span>}
                            {s.is_hidden && <span className="ml-1 text-[9px] bg-gray-800 text-white px-1 py-0.5 rounded">Oculto</span>}
                          </td>
                          <td className="px-2 py-2 text-right">
                            {s.is_empty ? (
                              <span className="text-[11px] text-gray-400 italic" data-testid={`sprint-nodata-${s.id}`}>Sin datos</span>
                            ) : (
                              <>
                                <span className={`font-bold ${s.completion_pct >= 80 ? "text-emerald-700" : s.completion_pct >= 40 ? "text-amber-700" : "text-red-600"}`}>
                                  {s.completion_pct}%
                                </span>
                                <div className="w-16 ml-auto mt-1"><MiniProgress pct={s.completion_pct} delayed={s.is_delayed}/></div>
                              </>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right text-gray-700 tabular-nums">
                            <div>{s.completed_items}/{s.total_items}</div>
                            <div className="text-[9px] text-gray-400">{s.remaining_items} abiertos</div>
                            {s.unassigned_items > 0 && (
                              <button onClick={() => openUnassigned(s)}
                                className="text-[9px] text-amber-700 hover:underline"
                                data-testid={`sprint-unassigned-btn-${s.id}`}>
                                {s.unassigned_items} sin asignar
                              </button>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right text-gray-600 tabular-nums text-[10px]">
                            {s.story_points_done}/{s.story_points_total}
                          </td>
                          <td className="px-2 py-2 text-center text-[10px] text-gray-500 whitespace-nowrap">
                            {s.start_date && s.end_date ? (
                              <>
                                <div>{s.start_date}</div>
                                <div>→ {s.end_date}</div>
                              </>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-center relative">
                            <button onClick={() => setMenuOpen(isMenuOpen ? null : s.id)} disabled={isBusy}
                              className="p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition disabled:opacity-40"
                              data-testid={`sprint-row-menu-btn-${s.id}`}
                              title="Acciones del sprint">
                              {isBusy ? <Loader2 size={12} className="animate-spin"/> : <MoreVertical size={12}/>}
                            </button>
                            {isMenuOpen && (
                              <>
                                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(null)} />
                                <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-52 text-xs text-left">
                                  {manualStatus === "paused" ? (
                                    <button onClick={() => setManualStatus(s.id, "none", "reanudar")}
                                      className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-emerald-700">
                                      <Play size={12}/> Reanudar sprint
                                    </button>
                                  ) : (
                                    <button onClick={() => setManualStatus(s.id, "paused", "pausar")}
                                      className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-amber-700">
                                      <Pause size={12}/> Pausar sprint
                                    </button>
                                  )}
                                  {manualStatus === "cancelled" ? (
                                    <button onClick={() => setManualStatus(s.id, "none", "reactivar")}
                                      className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-emerald-700">
                                      <RotateCcw size={12}/> Reactivar sprint
                                    </button>
                                  ) : (
                                    <button onClick={() => {
                                      if (window.confirm(`¿Cancelar Sprint ${s.sprint_number}: ${s.name}?`)) {
                                        setManualStatus(s.id, "cancelled", "cancelar");
                                      } else { setMenuOpen(null); }
                                    }}
                                      className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-rose-700">
                                      <XCircle size={12}/> Cancelar sprint
                                    </button>
                                  )}
                                  <div className="border-t border-gray-100 my-1"/>
                                  <button onClick={() => toggleHidden(s.id)}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-gray-700">
                                    {s.is_hidden ? <><Eye size={12}/> Mostrar</> : <><EyeOff size={12}/> Ocultar</>}
                                  </button>
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    };

                    // Iter209 · Modo agrupado por proyecto
                    if (groupByProject) {
                      const groups = {};
                      filtered.forEach(s => {
                        const pid = s.project_id || "__no_project__";
                        if (!groups[pid]) groups[pid] = { pid, title: s.project_title || "Sin proyecto", sprints: [] };
                        groups[pid].sprints.push(s);
                      });
                      const groupList = Object.values(groups).sort((a, b) => a.title.localeCompare(b.title));
                      return groupList.map(g => {
                        const total = g.sprints.length;
                        const active = g.sprints.filter(x => x.is_active).length;
                        const delayed = g.sprints.filter(x => x.is_delayed).length;
                        const avgPct = Math.round(g.sprints.reduce((a, x) => a + (x.completion_pct || 0), 0) / total);
                        const collapsed = collapsedGroups.has(g.pid);
                        return (
                          <React.Fragment key={g.pid}>
                            <tr className="bg-emerald-50/70 border-b-2 border-emerald-100 sticky top-0"
                                data-testid={`group-header-${g.pid}`}>
                              <td colSpan={7} className="px-3 py-2">
                                <button onClick={() => toggleGroup(g.pid)}
                                  className="w-full flex items-center gap-2 text-left text-emerald-800 font-semibold hover:text-emerald-600 transition"
                                  data-testid={`group-toggle-${g.pid}`}>
                                  {collapsed ? <ChevronRight size={14}/> : <ChevronDown size={14}/>}
                                  <FolderTree size={12}/>
                                  <span className="truncate flex-1">{g.title}</span>
                                  <span className="text-[10px] text-emerald-700 font-normal whitespace-nowrap">
                                    {total} sprints · {active} activos
                                    {delayed > 0 && <span className="ml-2 text-rose-700 font-semibold">· {delayed} retraso{delayed !== 1 ? "s" : ""}</span>}
                                    <span className="ml-2 text-gray-500">· avg {avgPct}%</span>
                                  </span>
                                </button>
                              </td>
                            </tr>
                            {!collapsed && g.sprints.map(renderSprintRow)}
                          </React.Fragment>
                        );
                      });
                    }
                    // Modo plano
                    return filtered.map(renderSprintRow);
                  })()}
                </tbody>
              </table>
            </div>
            <div className="text-[10px] text-gray-400 px-3 py-2 border-t border-gray-100 bg-gray-50/60">
              {filtered.length} sprint{filtered.length === 1 ? "" : "s"} · click en ⋯ para acciones (Pausar/Cancelar/Ocultar)
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="sprint-cards-grid">
            {filtered.map(s => {
              const cfg = STATUS_CFG[s.status] || STATUS_CFG.planning;
              const isMenuOpen = menuOpen === s.id;
              const isBusy = actionBusy === s.id;
              const manualStatus = (s.manual_status || "").toLowerCase();
              const hasManualOverride = ["paused", "cancelled", "active"].includes(manualStatus);
              const dimmed = s.is_hidden || s.status === "cancelled";
              return (
                <div key={s.id} className={`bg-white rounded-xl border transition-all hover:shadow-md group relative ${s.is_delayed ? "border-red-200" : "border-gray-100 hover:border-gray-200"} ${dimmed ? "opacity-60" : ""}`}
                  data-testid={`sprint-card-${s.id}`}>
                  {/* Card header */}
                  <div className={`px-4 py-3 border-b ${s.is_delayed ? "bg-red-50 border-red-100" : "bg-gray-50 border-gray-100"} rounded-t-xl`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-400 truncate">{s.project_title}</p>
                        <h3 className="text-sm font-bold text-gray-900 truncate mt-0.5">Sprint {s.sprint_number}: {s.name}</h3>
                      </div>
                      <div className="flex items-start gap-1 flex-shrink-0">
                        <div className="flex flex-col items-end gap-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.cls}`}>{cfg.label}</span>
                          {s.is_empty && <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-medium" data-testid={`sprint-empty-badge-${s.id}`}>Vacío</span>}
                          {s.is_delayed && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">Retrasado</span>}
                          {s.is_hidden && <span className="text-xs bg-gray-800 text-white px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><EyeOff size={9} /> Oculto</span>}
                          {hasManualOverride && <span className="text-[10px] text-gray-400 italic">override manual</span>}
                        </div>
                        <div className="relative">
                          <button onClick={() => setMenuOpen(isMenuOpen ? null : s.id)} disabled={isBusy}
                            className="p-1 rounded-md hover:bg-white/70 text-gray-500 hover:text-gray-800 transition disabled:opacity-40"
                            data-testid={`sprint-menu-btn-${s.id}`}
                            title="Acciones del sprint">
                            {isBusy ? <Loader2 size={14} className="animate-spin" /> : <MoreVertical size={14} />}
                          </button>
                          {isMenuOpen && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(null)} />
                              <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-52 text-xs"
                                data-testid={`sprint-menu-${s.id}`}>
                                {manualStatus === "paused" ? (
                                  <button onClick={() => setManualStatus(s.id, "none", "reanudar")}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-emerald-700"
                                    data-testid={`sprint-resume-${s.id}`}>
                                    <Play size={12} /> Reanudar sprint
                                  </button>
                                ) : (
                                  <button onClick={() => setManualStatus(s.id, "paused", "pausar")}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-amber-700"
                                    data-testid={`sprint-pause-${s.id}`}>
                                    <Pause size={12} /> Pausar sprint
                                  </button>
                                )}
                                {manualStatus === "cancelled" ? (
                                  <button onClick={() => setManualStatus(s.id, "none", "reactivar")}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-emerald-700"
                                    data-testid={`sprint-reactivate-${s.id}`}>
                                    <RotateCcw size={12} /> Reactivar sprint
                                  </button>
                                ) : (
                                  <button onClick={() => {
                                    if (window.confirm(`¿Cancelar Sprint ${s.sprint_number}: ${s.name}? Podrás reactivarlo después.`)) {
                                      setManualStatus(s.id, "cancelled", "cancelar");
                                    } else { setMenuOpen(null); }
                                  }}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-rose-700"
                                    data-testid={`sprint-cancel-${s.id}`}>
                                    <XCircle size={12} /> Cancelar sprint
                                  </button>
                                )}
                                {hasManualOverride && manualStatus !== "paused" && manualStatus !== "cancelled" && (
                                  <button onClick={() => setManualStatus(s.id, "none", "auto")}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-gray-600"
                                    data-testid={`sprint-clear-override-${s.id}`}>
                                    <RotateCcw size={12} /> Quitar override manual
                                  </button>
                                )}
                                <div className="border-t border-gray-100 my-1" />
                                <button onClick={() => toggleHidden(s.id)}
                                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                                  data-testid={`sprint-toggle-hidden-${s.id}`}>
                                  {s.is_hidden ? <><Eye size={12} /> Mostrar en dashboard</> : <><EyeOff size={12} /> Ocultar del dashboard</>}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {s.goal && <p className="text-xs text-gray-500 mt-1.5 truncate">{s.goal}</p>}
                  </div>
                  {/* Card body */}
                  <div className="px-4 py-3 space-y-3">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      {[
                        { label: "Total", val: s.total_items, cls: "text-gray-900" },
                        { label: "Hecho", val: s.completed_items, cls: "text-[#007934]" },
                        { label: "Pendiente", val: s.remaining_items, cls: s.remaining_items > 0 ? "text-amber-600" : "text-gray-400" },
                      ].map(k => (
                        <div key={k.label} className="bg-gray-50 rounded-lg py-2">
                          <p className={`text-lg font-bold font-['Outfit'] ${k.cls}`}>{k.val}</p>
                          <p className="text-[10px] text-gray-400">{k.label}</p>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-500">Progreso</span>
                        {!s.is_empty && (
                          <span className={`font-semibold ${s.is_delayed ? "text-red-600" : "text-[#007934]"}`}>{s.completion_pct}%</span>
                        )}
                      </div>
                      {s.is_empty ? (
                        <p className="text-[11px] text-gray-400 italic" data-testid={`sprint-nodata-${s.id}`}>Sin work items vinculados — no se puede medir progreso (no cuenta como 0%).</p>
                      ) : (
                        <MiniProgress pct={s.completion_pct} delayed={s.is_delayed} />
                      )}
                    </div>
                    {s.story_points_total > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400">Story Points</span>
                        <span className="font-medium text-gray-700">{s.story_points_done}/{s.story_points_total} SP</span>
                      </div>
                    )}
                    {(s.unassigned_items > 0 || (s.top_assignees || []).length > 0) && (
                      <div className="flex items-center justify-between text-xs">
                        {s.unassigned_items > 0 ? (
                          <button onClick={() => openUnassigned(s)}
                            className="text-amber-700 hover:underline font-medium"
                            data-testid={`sprint-unassigned-btn-${s.id}`}>
                            Sin asignar ({s.unassigned_items})
                          </button>
                        ) : <span/>}
                        {(s.top_assignees || [])[0] && (
                          <span className="text-gray-400 truncate max-w-[55%]" title={s.top_assignees[0].name}>
                            Top: {s.top_assignees[0].name} ({s.top_assignees[0].count})
                          </span>
                        )}
                      </div>
                    )}
                    {(s.start_date || s.end_date) && (
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock size={10} />
                        <span>{s.start_date || "—"} → {s.end_date || "—"}</span>
                      </div>
                    )}
                  </div>
                  {/* Card footer */}
                  <div className="px-4 py-2.5 border-t border-gray-50 flex items-center gap-2">
                    <button onClick={() => navigate(`/projects/${s.project_id}`)}
                      className="flex-1 text-xs text-center text-gray-500 hover:text-[#007934] py-1.5 rounded-lg hover:bg-[#f0fdf4] transition-all flex items-center justify-center gap-1"
                      data-testid={`go-to-project-${s.id}`}>
                      <ArrowRight size={11} /> Ver Sprint Board
                    </button>
                    {s.is_delayed && (
                      <button onClick={() => handleNotify(s.id)} disabled={notifying === s.id}
                        className="text-xs text-red-500 hover:text-red-700 py-1.5 px-2 rounded-lg hover:bg-red-50 transition-all flex items-center gap-1"
                        data-testid={`notify-btn-${s.id}`}>
                        {notifying === s.id ? <Loader2 size={10} className="animate-spin" /> : <Bell size={11} />}
                        Notificar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Iter258 (Mensaje 2) · Modal de work items sin asignar de un sprint */}
      {unassignedModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setUnassignedModal(null)} data-testid="unassigned-workitems-modal">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[70vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Work items sin asignar</h3>
                <p className="text-xs text-gray-400 truncate max-w-sm">{unassignedModal.sprintName}</p>
              </div>
              <button onClick={() => setUnassignedModal(null)} className="text-gray-400 hover:text-gray-700" data-testid="close-unassigned-modal">
                <XCircle size={18}/>
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {unassignedModal.loading ? (
                <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-gray-400"/></div>
              ) : unassignedModal.items.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">Sin work items pendientes de asignar.</p>
              ) : (
                <div className="space-y-2">
                  {unassignedModal.items.map(it => (
                    <div key={it.id || it.azure_id} className="border border-gray-100 rounded-lg px-3 py-2 text-xs" data-testid={`unassigned-wi-${it.id || it.azure_id}`}>
                      <div className="font-semibold text-gray-800">{it.title || "(sin título)"}</div>
                      <div className="text-gray-400 mt-0.5">{it.type} · {it.status}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
